import { useState, useEffect, useCallback } from 'react';
import { useMedplum, useSubscription } from '@medplum/react';
import { Practitioner, PractitionerRole, Patient } from '@medplum/fhirtypes';
import { formatHumanName } from '@medplum/core';
import { ARKON } from '@consts/systems.consts';

//------------------------------------------------------------------------------
const PATIENT_ASSIGNMENT_IDENTIFIER_SYSTEM = 'patient-assignment';
//------------------------------------------------------------------------------
export interface AssignedDoctor {
  id: string;
  practitioner: Practitioner;
  practitionerRole?: PractitionerRole;
  role?: string;
}

//------------------------------------------------------------------------------
export const useAssignedDoctors = (patient: Patient | undefined) => {
  const medplum = useMedplum();
  const [assignedDoctors, setAssignedDoctors] = useState<AssignedDoctor[]>([]);

  //----------------------------------------------------------------------------
  // Load assigned doctors from patient generalPractitioner field and PractitionerRole resources

  const loadAssignedDoctors = useCallback(async () => {
    if (!patient) {
      setAssignedDoctors([]);
      return;
    }

    const doctors: AssignedDoctor[] = [];

    // Load from generalPractitioner field
    if (patient.generalPractitioner) {
      for (const practitionerRef of patient.generalPractitioner) {
        if (practitionerRef.reference?.startsWith('Practitioner/')) {
          try {
            const practitioner = await medplum.readResource(
              'Practitioner',
              practitionerRef.reference.split('/')[1]
            );
            if (practitioner.id) {
              doctors.push({
                id: practitioner.id,
                practitioner,
                role: 'General Practitioner',
              });
            }
          } catch (error) {
            console.error('Error loading practitioner:', error);
          }
        }
      }
    }

    // Load from PractitionerRole resources for this patient
    try {
      const practitionerRoles = await medplum.searchResources(
        'PractitionerRole',
        {
          identifier: `${PATIENT_ASSIGNMENT_IDENTIFIER_SYSTEM}|${patient.id}`,
          _include: 'PractitionerRole:practitioner',
        }
      );

      for (const role of practitionerRoles) {
        if (role.practitioner?.reference?.startsWith('Practitioner/')) {
          try {
            const practitioner = await medplum.readResource(
              'Practitioner',
              role.practitioner.reference.split('/')[1]
            );
            if (practitioner.id) {
              // Determine role from PractitionerRole code
              const roleCode = role.code?.[0]?.coding?.[0]?.code;
              const roleDisplay =
                role.code?.[0]?.coding?.[0]?.display ||
                (roleCode === 'referral'
                  ? 'Referral Doctor'
                  : 'Assigned Doctor');

              doctors.push({
                id: practitioner.id,
                practitioner,
                practitionerRole: role,
                role: roleDisplay,
              });
            }
          } catch (error) {
            console.error('Error loading practitioner from role:', error);
          }
        }
      }
    } catch (error) {
      console.error('Error loading practitioner roles:', error);
    }

    setAssignedDoctors(doctors);
  }, [patient, medplum]);

  useEffect(() => {
    loadAssignedDoctors();
  }, [loadAssignedDoctors]);
  //----------------------------------------------------------------------------
  // Subscribe to PractitionerRole changes
  useSubscription(
    patient?.id
      ? `PractitionerRole?identifier=${PATIENT_ASSIGNMENT_IDENTIFIER_SYSTEM}|${patient.id}`
      : '',
    (_bundle) => {
      // Reload all assigned doctors when any PractionerRole changes
      loadAssignedDoctors();
    }
  );

  //----------------------------------------------------------------------------
  const addDoctor = useCallback(
    async (
      practitioner: Practitioner,
      role: 'assigned' | 'referral' = 'assigned'
    ) => {
      if (!patient || !practitioner.id) {
        return;
      }

      try {
        if (role === 'assigned') {
          // For general practitioners, update the patient's generalPractitioner field
          const updatedPractitioners = patient.generalPractitioner || [];

          // Check if already exists
          const existingIndex = updatedPractitioners.findIndex(
            (p) => p.reference === `Practitioner/${practitioner.id}`
          );

          if (existingIndex === -1) {
            updatedPractitioners.push({
              reference: `Practitioner/${practitioner.id}`,
              display: formatHumanName(practitioner.name?.[0]),
            });

            await medplum.updateResource({
              ...patient,
              generalPractitioner: updatedPractitioners,
            });

            setAssignedDoctors((prev) => [
              ...prev,
              {
                id: practitioner.id || '',
                practitioner,
                role: 'General Practitioner',
              },
            ]);
          }
        } else {
          // For referral doctors, check if already exists first
          const existingDoctor = assignedDoctors.find(
            (d) => d.id === practitioner.id && d.role === 'Referral Doctor'
          );

          if (existingDoctor) {
            // Doctor is already assigned as a referral doctor
            return;
          }

          // Create a PractitionerRole resource for the referral doctor
          const practitionerRole: PractitionerRole = {
            resourceType: 'PractitionerRole',
            practitioner: {
              reference: `Practitioner/${practitioner.id}`,
              display: formatHumanName(practitioner.name?.[0]),
            },
            code: [
              {
                coding: [
                  {
                    system: ARKON.CS.PRACTITIONER.ROLE.BASE,
                    code: role,
                    display:
                      role === ARKON.CS.PRACTITIONER.ROLE.CODES.REFERRAL
                        ? 'Referral Doctor'
                        : 'Assigned Doctor',
                  },
                ],
              },
            ],
            identifier: [
              {
                system: 'patient-assignment',
                value: patient.id,
              },
            ],
            active: true,
          };

          const createdRole = await medplum.createResource(practitionerRole);

          setAssignedDoctors((prev) => [
            ...prev,
            {
              id: practitioner.id || '',
              practitioner,
              practitionerRole: createdRole,
              role:
                role === ARKON.CS.PRACTITIONER.ROLE.CODES.REFERRAL
                  ? 'Referral Doctor'
                  : 'Assigned Doctor',
            },
          ]);
        }
      } catch (error) {
        console.error('Error assigning doctor:', error);
      }
    },
    [patient, medplum, assignedDoctors]
  );

  //----------------------------------------------------------------------------
  const removeDoctor = useCallback(
    async (doctorId: string) => {
      if (!patient) {
        return;
      }

      try {
        const doctorToRemove = assignedDoctors.find((d) => d.id === doctorId);
        if (doctorToRemove?.practitionerRole?.id) {
          // Remove PractitionerRole resource for referral/assigned doctors
          await medplum.deleteResource(
            'PractitionerRole',
            doctorToRemove.practitionerRole.id
          );
        } else {
          // Remove from patient's generalPractitioner field
          const updatedPractitioners = (
            patient.generalPractitioner || []
          ).filter((p) => p.reference !== `Practitioner/${doctorId}`);

          await medplum.updateResource({
            ...patient,
            generalPractitioner:
              updatedPractitioners.length > 0
                ? updatedPractitioners
                : undefined,
          });
        }

        setAssignedDoctors((prev) => prev.filter((d) => d.id !== doctorId));
      } catch (error) {
        console.error('Error removing doctor:', error);
      }
    },
    [patient, medplum, assignedDoctors]
  );

  //----------------------------------------------------------------------------
  return {
    assignedDoctors,
    addDoctor,
    removeDoctor,
  };
};
